import { randomBetween } from '../helpers';

const FULL_TURN = Math.PI * 2;

const ICE_RGB = '212, 223, 234';

const SPECK_SIZE = 32;

const CLUMP = {
  size: 64,
  variants: 6,
  blobCount: { min: 3, max: 6 },
  blobRadius: { min: 0.18, max: 0.27 },
  blobSpread: 0.12,
  highlightRgb: '238, 243, 248',
  highlight: { shiftX: -0.2, shiftY: -0.25, scale: 0.5, alpha: 0.8 },
  shadowRgb: '30, 50, 80',
  shadow: { offset: 0.06, spread: 1.1, alpha: 0.2, core: 0.3 },
};

const BLUR = {
  size: 128,
  falloff: [
    { stop: 0, alpha: 0.85 },
    { stop: 0.4, alpha: 0.7 },
    { stop: 0.65, alpha: 0.35 },
    { stop: 0.85, alpha: 0.1 },
    { stop: 1, alpha: 0 },
  ],
};

interface SoftCircleArgs {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  radius: number;
  rgb: string;
  alpha: number;
  core?: number;
}

interface SpriteArgs {
  size: number;
  paint: (context: CanvasRenderingContext2D) => void;
}

interface ClumpBlob {
  x: number;
  y: number;
  radius: number;
}

const paintSoftCircle = ({ context, x, y, radius, rgb, alpha, core = 0.55 }: SoftCircleArgs) => {
  const gradient = context.createRadialGradient(x, y, 0, x, y, radius);

  gradient.addColorStop(0, `rgba(${rgb}, ${alpha})`);
  gradient.addColorStop(core, `rgba(${rgb}, ${alpha})`);
  gradient.addColorStop(1, `rgba(${rgb}, 0)`);
  context.fillStyle = gradient;
  context.beginPath();
  context.arc(x, y, radius, 0, FULL_TURN);
  context.fill();
};

const createSprite = ({ size, paint }: SpriteArgs) => {
  const sprite = document.createElement('canvas');

  sprite.width = size;
  sprite.height = size;

  const context = sprite.getContext('2d');

  if (context) {
    paint(context);
  }

  return sprite;
};

const createClumpBlob = (): ClumpBlob => {
  const angle = Math.random() * FULL_TURN;
  const distance = Math.random() * CLUMP.size * CLUMP.blobSpread;

  return {
    x: CLUMP.size / 2 + Math.cos(angle) * distance,
    y: CLUMP.size / 2 + Math.sin(angle) * distance,
    radius: CLUMP.size * randomBetween(CLUMP.blobRadius),
  };
};

const createClumpSprite = () =>
  createSprite({
    size: CLUMP.size,
    paint: (context) => {
      const blobs = Array.from(
        { length: Math.floor(randomBetween(CLUMP.blobCount)) },
        createClumpBlob
      );
      const { shadow, highlight } = CLUMP;

      blobs.forEach(({ x, y, radius }) =>
        paintSoftCircle({
          context,
          x,
          y: y + CLUMP.size * shadow.offset,
          radius: radius * shadow.spread,
          rgb: CLUMP.shadowRgb,
          alpha: shadow.alpha,
          core: shadow.core,
        })
      );
      blobs.forEach(({ x, y, radius }) =>
        paintSoftCircle({ context, x, y, radius, rgb: ICE_RGB, alpha: 1 })
      );
      blobs.forEach(({ x, y, radius }) =>
        paintSoftCircle({
          context,
          x: x + radius * highlight.shiftX,
          y: y + radius * highlight.shiftY,
          radius: radius * highlight.scale,
          rgb: CLUMP.highlightRgb,
          alpha: highlight.alpha,
        })
      );
    },
  });

export const createSnowSprites = () => ({
  speck: createSprite({
    size: SPECK_SIZE,
    paint: (context) =>
      paintSoftCircle({
        context,
        x: SPECK_SIZE / 2,
        y: SPECK_SIZE / 2,
        radius: SPECK_SIZE / 2,
        rgb: ICE_RGB,
        alpha: 1,
      }),
  }),
  clumps: Array.from({ length: CLUMP.variants }, createClumpSprite),
  blur: createSprite({
    size: BLUR.size,
    paint: (context) => {
      const center = BLUR.size / 2;
      const gradient = context.createRadialGradient(center, center, 0, center, center, center);

      BLUR.falloff.forEach(({ stop, alpha }) =>
        gradient.addColorStop(stop, `rgba(${ICE_RGB}, ${alpha})`)
      );
      context.fillStyle = gradient;
      context.fillRect(0, 0, BLUR.size, BLUR.size);
    },
  }),
});
